/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback } from "react"

import { AxiosError } from "axios"
import eq from "lodash-es/eq"
import isNumber from "lodash-es/isNumber"
import { useUnmount } from "react-use"

import { generateUuid, reduxBatch } from "@/3514/utils"

import {
    type TCoachingChatCreateMessageMutation as TCreateMessageMutation,
    type TCoachingChatCreateMessageMutationContext as TCreateMessageMutationContext,
    type TCoachingChatCreateMessageMutationParams as TCreateMessageMutationParams,
    type TCoachingChatCreateMessageMutationResponse as TCreateMessageMutationResponse,
    useCoachingChatCreateMessageMutation as useCreateMessageMutation,
    useCoachingChatSocketEmitter as useEmitter
} from "../api"
import {
    useCoachingChatConversationApiMode as useConversationApiMode,
    useCoachingChatConversationDispatcher as useConversationDispatcher,
    useCoachingChatConversationIdSelector as useConversationIdSelector
} from "../store"
import {
    ECoachingChatMessageAuthor as EChatMessageAuthor,
    ECoachingChatConversationApiMode as EConversationApiMode,
    ECoachingChatConversationErrorVariant as EConversationErrorVariant,
    ECoachingChatConversationMessagePhase as EConversationMessagePhase,
    ECoachingChatConversationStatus as EConversationStatus,
    ECoachingChatMessageTransport as EMessageTransport,
    ECoachingChatSocketEmitterEventName as ESockerEmitterEventName,
    type TCoachingChatSocketMessageEventPayload as TMessageEventPayload,
    type _TCoachingChatHookSharedParams as _TSharedParams
} from "../types"
import { coachingChatUiUtils as uiUtils } from "../utils"

const {
    tabId: { get: getTabId },
    queriesModelsDataExtractors: { getConversationErrorVariantByAxiosError: getChatErrorVariantFromAxiosError }
} = uiUtils

type _TParams = Pick<_TSharedParams, "entityId">

type TCoachingChatConversationMessageSendFunction = (
    params: TCreateMessageMutationParams,
    onSettled: (data: never, err: AxiosError) => Promise<void>
) => Promise<void>

function useCoachingChatConversationMessageSender({
    entityId
}: _TParams): ReturnType<(params: _TParams) => TCoachingChatConversationMessageSendFunction> {
    const conversationId: number | null = useConversationIdSelector(entityId)

    const conversationDispatcher: ReturnType<typeof useConversationDispatcher> = useConversationDispatcher(entityId)

    const conversationApiMode: EConversationApiMode = useConversationApiMode()

    const isRestMode: boolean = eq(conversationApiMode, EConversationApiMode.Rest)
    const isSocketMode: boolean = eq(conversationApiMode, EConversationApiMode.Sockets)

    const { mutateAsync: sendMessageViaRest, reset: resetMutation }: TCreateMessageMutation = useCreateMessageMutation({
        onMutate: async ({
            conversationId: createdMessageConversationId,
            message: createdMessageText
        }: TCreateMessageMutationParams): Promise<TCreateMessageMutationContext> => {
            const userMessageId: string = generateUuid()
            const answerMessageId: string = generateUuid()

            reduxBatch(
                (): void => (
                    conversationDispatcher.clearError(),
                    conversationDispatcher.markInput({ isDisabled: true, isDimmed: true }),
                    conversationDispatcher.changeStatus(EConversationStatus.InProgress),
                    conversationDispatcher.addMessage({
                        id: Date.now() - 100,
                        transport: EMessageTransport.Rest,
                        conversationId: createdMessageConversationId,
                        content: createdMessageText,
                        author: EChatMessageAuthor.User,
                        clientMessageId: userMessageId,
                        isStreamable: false,
                        status: EConversationMessagePhase.Done
                    }),
                    conversationDispatcher.addMessage({
                        id: Date.now(),
                        transport: EMessageTransport.Rest,
                        conversationId: createdMessageConversationId,
                        content: String(""),
                        author: EChatMessageAuthor.AI,
                        clientMessageId: answerMessageId,
                        isStreamable: false,
                        status: EConversationMessagePhase.Waiting
                    })
                )
            )

            return { sentMessageId: userMessageId, placeholderMessageId: answerMessageId }
        },
        onSuccess: async (
            response: TCreateMessageMutationResponse,
            _vars: TCreateMessageMutationParams,
            { sentMessageId }: TCreateMessageMutationContext
        ): Promise<void> => {
            if (isNumber(response?.data?.message_id))
                reduxBatch(
                    (): void => (
                        conversationDispatcher.replaceTempMessage(response.data.message_id, sentMessageId),
                        conversationDispatcher.markInput({ isDisabled: false, isDimmed: false })
                    )
                )
        },
        onError: async (
            error: AxiosError,
            _vars: TCreateMessageMutationParams,
            { sentMessageId, placeholderMessageId }: TCreateMessageMutationContext
        ): Promise<void> => {
            const errVariant: EConversationErrorVariant | undefined = getChatErrorVariantFromAxiosError(error)

            reduxBatch(
                (): void => (
                    conversationDispatcher.markInput({ isDisabled: !!errVariant, isDimmed: !!errVariant }),
                    conversationDispatcher.removeMessageByClientId(sentMessageId),
                    conversationDispatcher.removeMessageByClientId(placeholderMessageId),
                    conversationDispatcher.triggerError(errVariant),
                    conversationDispatcher.changeStatus(EConversationStatus.Paused)
                )
            )
        }
    })

    const { emit: sendMessageViaSocket }: ReturnType<typeof useEmitter<TMessageEventPayload>> =
        useEmitter<TMessageEventPayload>(ESockerEmitterEventName.Message)

    useUnmount((): void => {
        resetMutation()
    })

    return useCallback(
        async (
            params: TCreateMessageMutationParams,
            onSettled: (data: never, err: AxiosError) => Promise<void>
        ): Promise<void> => {
            if (isRestMode) {
                await sendMessageViaRest(params, { onSettled })
            } else if (isSocketMode) {
                const userMessageId: string = generateUuid()
                const botMessageId: string = generateUuid()

                await sendMessageViaSocket(
                    {
                        conversation_id: conversationId,
                        text: params.message,
                        tab_uuid: getTabId(),
                        client_message_uuid: userMessageId,
                        transport: params.transport
                    },
                    {
                        onEmit: (vars: TMessageEventPayload): void => {
                            reduxBatch(
                                (): void => (
                                    conversationDispatcher.markInput({ isDisabled: true, isDimmed: true }),
                                    conversationDispatcher.clearError(),
                                    conversationDispatcher.changeStatus(EConversationStatus.InProgress),
                                    conversationDispatcher.addMessage({
                                        id: Date.now() - 100,
                                        transport: EMessageTransport.Sockets,
                                        conversationId: vars.conversation_id,
                                        content: vars.text,
                                        author: EChatMessageAuthor.User,
                                        clientMessageId: userMessageId,
                                        isStreamable: false,
                                        status: EConversationMessagePhase.Done
                                    }),
                                    conversationDispatcher.addMessage({
                                        id: Date.now(),
                                        transport: EMessageTransport.Sockets,
                                        conversationId: vars.conversation_id,
                                        content: String(""),
                                        author: EChatMessageAuthor.AI,
                                        clientMessageId: botMessageId,
                                        isStreamable: true,
                                        status: EConversationMessagePhase.Waiting
                                    })
                                )
                            )
                        },
                        onSuccess: (): void => conversationDispatcher.changeStatus(EConversationStatus.InProgress),
                        onError: (): void =>
                            reduxBatch(
                                (): void => (
                                    conversationDispatcher.markInput({ isDimmed: false, isDisabled: false }),
                                    conversationDispatcher.removeMessageByClientId(userMessageId),
                                    conversationDispatcher.removeMessageByClientId(botMessageId),
                                    conversationDispatcher.triggerError(EConversationErrorVariant.ApiFailure),
                                    conversationDispatcher.changeStatus(EConversationStatus.Paused)
                                )
                            ),
                        onSettled: (): Promise<void> => onSettled(void 0 as never, null)
                    }
                )
            }
        },
        [sendMessageViaRest, sendMessageViaSocket, isSocketMode, isRestMode, conversationId]
    )
}

export { useCoachingChatConversationMessageSender }
export type { TCoachingChatConversationMessageSendFunction }
